import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Bits";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, FileOutput } from "lucide-react";

const TYPE = { hours: "Hours", quantity: "Delivered Qty", milestone: "Milestone", extra: "Agreed Extra" };

export default function ReadyToInvoice() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState({});

  const load = useCallback(() => { api.get("/ready-to-invoice").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    try { await api.patch(`/ready-to-invoice/${id}`, { status }); toast.success(`Item ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const approvedByJob = {};
  items.filter((i) => i.status === "approved").forEach((i) => {
    (approvedByJob[i.job_id] = approvedByJob[i.job_id] || { job_number: i.job_number, job_name: i.job_name, customer_name: i.customer_name, items: [] }).items.push(i);
  });

  const makeInvoice = async (jobId, its) => {
    const ids = its.filter((i) => sel[i.id]).map((i) => i.id);
    const rti_ids = ids.length ? ids : its.map((i) => i.id);
    try {
      const { data } = await api.post("/invoices", { job_id: jobId, rti_ids });
      toast.success(`Created ${data.invoice_number}`);
      nav("/invoices");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pending = items.filter((i) => i.status === "pending");

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Ready to Invoice" subtitle="Manager approves hours, delivered quantities, milestones and extras before billing" />

      <h3 className="font-heading font-bold uppercase text-sm mb-3">Pending Approval ({pending.length})</h3>
      {pending.length === 0 ? <Empty text="Nothing awaiting approval." /> : (
        <div className="space-y-2 mb-8">
          {pending.map((i) => (
            <div key={i.id} className="card-industrial p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`rti-${i.id}`}>
              <div className="flex-1 min-w-[200px]">
                <span className="text-sm"><span className="text-muted-foreground">{i.customer_name} · </span><span className="font-semibold">{i.job_name || i.job_number}</span> <span className="font-mono-num text-[10px] text-muted-foreground">{i.job_number}</span></span>
                <span className="text-xs ml-2 px-2 py-0.5 rounded bg-accent text-muted-foreground">{TYPE[i.type] || i.type}</span>
                <div className="text-sm mt-1">{i.description}</div>
                <div className="text-xs text-muted-foreground">{i.customer_name}</div>
              </div>
              <span className="font-mono-num font-bold">{money(i.amount)}</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => decide(i.id, "approved")} data-testid={`rti-approve-${i.id}`} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => decide(i.id, "rejected")} data-testid={`rti-reject-${i.id}`}><X className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 className="font-heading font-bold uppercase text-sm mb-3">Approved — Ready to Bill</h3>
      {Object.keys(approvedByJob).length === 0 ? <Empty text="No approved items yet." /> : (
        <div className="space-y-4">
          {Object.entries(approvedByJob).map(([jobId, g]) => (
            <div key={jobId} className="card-industrial p-4" data-testid={`approved-group-${g.job_number}`}>
              <div className="flex items-center justify-between mb-3">
                <div><span className="text-sm text-muted-foreground">{g.customer_name} · </span><span className="font-semibold">{g.job_name || g.job_number}</span><span className="font-mono-num text-[10px] text-muted-foreground ml-2">{g.job_number}</span></div>
                <Button size="sm" onClick={() => makeInvoice(jobId, g.items)} data-testid={`make-invoice-${g.job_number}`} className="font-bold uppercase"><FileOutput className="w-4 h-4 mr-1" />Create Invoice</Button>
              </div>
              <ul className="space-y-1.5">
                {g.items.map((i) => (
                  <li key={i.id} className="flex items-center gap-3 text-sm">
                    <Checkbox checked={!!sel[i.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [i.id]: v }))} data-testid={`sel-${i.id}`} />
                    <span className="flex-1">{i.description}</span>
                    <span className="font-mono-num">{money(i.amount)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-muted-foreground mt-2">Tick items to bill a subset, or create an invoice for all.</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
